<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Test Package Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <b-form @submit.prevent="onSubmit">
      <b-row class="mt-4">
        <b-col>
          <b-card no-body>
            <b-card-header>
              <span class="text-muted text-uppercase"
                >Test Package Identification</span
              >
            </b-card-header>
            <div class="py-3 px-5">
              <b-form-group label="Name" :label-cols="2">
                <b-form-input
                  type="text"
                  v-model.trim="form_data.name"
                  placeholder="Test Package Name"
                />
              </b-form-group>
              <b-form-group label="Sample Type" :label-cols="2">
                <div>
                  <multiselect
                    v-model="selected_sample_type"
                    :value="sample_type_value"
                    :options="sample_type_options"
                    :multiple="false"
                    track-by="id"
                    label="name"
                  />
                </div>
              </b-form-group>
              <b-form-group label="Group" :label-cols="2">
                <div>
                  <multiselect
                    v-model="selected_test_package_group"
                    :disabled="!form_data.sample_type_id"
                    :value="test_package_group_value"
                    :options="test_package_group_options"
                    :multiple="false"
                    track-by="id"
                    label="name"
                  />
                </div>
              </b-form-group>
              <b-form-group label="Description" :label-cols="2">
                <b-form-textarea
                  v-model="form_data.description"
                  placeholder="Test Package Description"
                />
              </b-form-group>
              <b-form-group label="Package Cost" :label-cols="2">
                <b-input-group prepend="$">
                  <b-form-input
                    type="number"
                    v-model="form_data.cost"
                    placeholder="Cost"
                  />
                </b-input-group>
              </b-form-group>
              <b-form-group label="Add-on Cost" :label-cols="2">
                <b-input-group prepend="$">
                  <b-form-input
                    type="number"
                    v-model="form_data.cost_add_on"
                    placeholder="Add-on Cost"
                  />
                </b-input-group>
              </b-form-group>
              <b-form-group label="Customer Access" :label-cols="2">
                <b-form-checkbox
                  v-model="selected_private"
                  :value="1"
                  :unchecked-value="0"
                  switch
                >
                  {{ form_data.private ? " Private" : "Public" }}
                </b-form-checkbox>
              </b-form-group>
              <b-form-group label="Internal" :label-cols="2">
                <b-form-checkbox
                  v-model="form_data.internal"
                  :value="1"
                  :unchecked-value="0"
                  switch
                />
              </b-form-group>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-card v-if="form_data.private" no-body>
        <b-card-header>Customer Access Selector</b-card-header>
        <b-card-body>
          <b-row>
            <b-col>
              <b-form-group label="Customer Search" label-cols="2">
                <b-form-input
                  v-model="filter"
                  placeholder="Search for a customer..."
                  debounce="300"
                />
                <b-alert v-if="!filter" show class="py-1 m-0 mt-3"
                  >{{ customer_options.length ? "Choose" : "Search" }} a
                  customer.</b-alert
                >
                <b-row v-if="filter && !customer_options.length">
                  <b-col class="pr-2">
                    <b-alert show class="py-1 m-0 mt-3"
                      >No matches found.</b-alert
                    >
                  </b-col>
                </b-row>
                <custom-form-checkbox-group
                  v-if="customer_options.length"
                  v-model="form_data.customers"
                  :options="customer_options"
                  @change="(event) => updateCustomerAccess(event)"
                  class="mt-3"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
      <b-row>
        <b-col>
          <test-package-form-sample-card
            v-for="(package_sample, index) in form_data.test_package_samples"
            :key="index"
            :test_package="test_package"
            :package_sample="package_sample"
            :sample_type_analytes="analyte_collection"
            :parent_index="index"
            @tagged="handleTagged"
            @updatePackageSample="updatePackageSample"
            @deletePackageSample="deletePackageSample"
          />
          <b-button
            variant="outline-primary"
            block
            @click="addEmptyPackageSample"
          >
            <i class="fal fa-plus" /> Add Another Package Sample
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-button
            @click="submitForm"
            class="my-4 float-right py-3 px-5"
            variant="success"
          >
            <i class="fal fa-address-book" /> Save</b-button
          >
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import TestPackageCard from "@/components/lab/testpackage/TestPackageCard";
import TestPackageFormSampleCard from "@/components/lab/testpackage/form/TestPackageFormSampleCard";
import CustomFormCheckboxGroup from "@/components/shared/form/CustomFormCheckboxGroup";
import { getCollection } from "@/shared/fetch-utils";

export default {
  name: "TestPackageForm",
  components: {
    TestPackageCard,
    TestPackageFormSampleCard,
    Multiselect,
    CustomFormCheckboxGroup,
  },
  props: {
    test_package: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: true,
      form_data: {
        sample_type_id: null,
        test_package_group_id: null,
        name: null,
        cost: null,
        cost_add_on: null,
        description: null,
        private: 0,
        customers: [],
        test_package_samples: [],
        internal: 0
      },
      analyte_collection: null,
      customer_search_input: null,
      customer_options: [],
      sample_type_collection: null,
      sample_type_options: [],
      sample_type_value: null,
      test_package_group_collection: null,
      test_package_group_options: [],
      test_package_group_value: null,
      payload: {
        message: this.$bvToast,
      },
    };
  },
  computed: {
    selected_sample_type: {
      get() {
        return this.sample_type_value;
      },
      set(value) {
        this.sample_type_value = value;
        this.form_data.sample_type_id = value && value.id;
        if (value && value.id) {
          this.getTestPackageGroups(value.id);
          this.getAnalytes(value.id);
        }
      },
    },
    selected_test_package_group: {
      get() {
        return this.test_package_group_value;
      },
      set(value) {
        this.test_package_group_value = value;
        this.form_data.test_package_group_id = value && value.id;
      },
    },
    selected_private: {
      get() {
        return this.form_data.private;
      },
      set(value) {
        this.form_data.private = value;
        !value && (this.form_data.customers = []);
      },
    },
    filter: {
      get() {
        return this.customer_search_input;
      },
      set(value) {
        this.customer_search_input = value;
        this.loadCustomers();
      },
    },
  },
  methods: {
    async initFormData() {
      const {
        customers,
        description,
        sample_type_id,
        test_package_group_id,
        test_package_samples,
      } = this.test_package;

      this.form_data = {
        ...this.test_package.cloneAttributes(true, true, [
          "add_on",
          "id",
          "position",
        ]),
        customers: customers.map(({ id }) => id),
        test_package_samples: test_package_samples.map(({ name, analytes, test_package_analytes }) => {
          const analytes_payload = analytes.map(({ id }) => { return { id } });
          test_package_analytes.forEach((element) => {
            analytes_payload.find((o, i) => {
              if (o.id === element.analyte_id) {
                analytes_payload[i]['attribute_list_id'] = element.attribute_list_id;
                if (element.attribute_list) {
                  analytes_payload[i]['managed_list_id'] = element.attribute_list.managed_list_id;
                  analytes_payload[i]['parent_id'] = element.attribute_list.parent_id;
                }
                return true;
              }
            })
          });
          return {
            name,
            analytes: analytes_payload,
          };
        }),
        description,
        test_package_group_id,
      };

      if (sample_type_id) {
        this.sample_type_value = this.sample_type_options.find(
          ({ id }) => id === sample_type_id
        );
        await this.getTestPackageGroups(sample_type_id);
        await this.getAnalytes(sample_type_id);
      }

      test_package_group_id &&
        (this.test_package_group_value = this.test_package_group_options.find(
          ({ id }) => id === test_package_group_id
        ));
    },
    async getAnalytes(sample_type_id) {
      this.analyte_collection = await getCollection({
        ...this.payload,
        collection_type: "analyte",
        filters: [
          {
            logic: "filterEquals",
            field: "sample_type_id",
            value: sample_type_id,
          },
        ],
        sort: [
          {
            logic: "sortAddAsc",
            field: "abbrev",
          },
        ],
        limit: "all",
      });
    },
    async getTestPackageGroups(sample_type_id) {
      this.test_package_group_collection = await getCollection({
        ...this.payload,
        collection_type: "test_package_group",
        filters: [
          {
            logic: "filterEquals",
            field: "sample_type_id",
            value: sample_type_id,
          },
        ],
        sort: [
          {
            logic: "sortAddDesc",
            field: "name",
          },
        ],
        limit: "all",
      });

      this.test_package_group_options =
        this.test_package_group_collection.models.map(({ id, name }) => {
          return {
            id,
            name,
          };
        });
    },
    async init() {
      this.sample_type_collection = await getCollection({
        ...this.payload,
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        collection_type: "sample_type",
        limit: "all",
      });
      this.sample_type_options = this.sample_type_collection.models.map(
        ({ id, name }) => {
          return {
            id,
            name,
          };
        }
      );
      this.test_package && this.initFormData();
      this.loading = false;
    },
    updateCustomerAccess(value) {
      this.form_data.customers = value;
    },
    async loadCustomers() {
      try {
        const collection = await getCollection({
          ...this.payload,
          collection_type: "customer",
          filters: [
            {
              logic: "filterContains",
              field: "short_name",
              value: this.filter,
            },
            {
              logic: "setLimit",
              value: 10,
            },
          ],
        });

        this.customer_options = collection.models.map(({ short_name, id }) => ({
          text: short_name,
          value: id,
          icon: "user",
        }));
      } catch ({ response }) {
        console.log(`Error`, response.data.error);
      }
    },
    addEmptyPackageSample() {
      this.form_data.test_package_samples = [
        ...this.form_data.test_package_samples,
        {
          name: null,
          analytes: [],
        },
      ];
    },
    handleTagged(payload) {
      if (!payload.length) {
        return;
      }

      payload.forEach((val) => {
        // find the associated analyte that we 'tagged' and set the attribute value in the payload
        this.form_data.test_package_samples[val.parent_index].analytes.find((search) => search.id === val.index)['attribute_list_id'] = val.attribute_value.id;
      });
    },
    updatePackageSample({ index, field, value }) {
      this.form_data.test_package_samples =
        this.form_data.test_package_samples.map((sample, sample_index) => {
          if (sample_index === index) {
            
            // set the default payload
            // this will only stay like this for 'name'
            let payload = {
              ...sample,
              [field]: value
            };
            // properly format the value
            if (typeof value === 'object') {
              // value is now an array
              // when looking at it before it will look like an array but be typed as an object, this just ensures its an array
              value = [...value];

              // update the payload vlaue to use the new array
              payload = {
                ...sample,
                [field]: value
              };

              /** 
               * * we only want to do this in value as an object because the name field also uses 
               * * this method to populate the payload but will pass in as a string
               * 
               * * We can now assume we are working with analytes
              */
              if (this.form_data.test_package_samples[sample_index][field].length && value.length) {  
                  
                  // This is to help us handle unchecking an analyte
                  if (value.length < this.form_data.test_package_samples[sample_index][field].length) {
                    // create a copy of the current payload 
                    const shallow_copy = this.form_data.test_package_samples[sample_index][field];
                    // reset the payload to the incoming value
                    this.form_data.test_package_samples[sample_index][field] = value;
                    
                    // for each of the values in the copy
                    shallow_copy.forEach((el) => {
                      // try and find it in the updated payload
                      this.form_data.test_package_samples[sample_index][field].find((o, i) => {
                        // check for id's to match as this can only happen once per index
                        // make sure we have an attribute_list_id on the copy else we don't care
                        if (o.id === el.id && el.attribute_list_id) {
                          // set the attribute list id to the updated payload
                          this.form_data.test_package_samples[sample_index][field][i]['attribute_list_id'] = el.attribute_list_id;
                          // stop searching
                          return true;
                        }
                      })
                    });
                  } else {
                    // for each of the incoming value
                    value.forEach((val, idx) => {
                      // check if the value already exists
                      if (this.form_data.test_package_samples[sample_index][field][idx]) {
                        // update it
                        this.form_data.test_package_samples[sample_index][field][idx]['id'] = val.id;
                      } else {
                        // create it
                        this.form_data.test_package_samples[sample_index][field][idx] = val;
                      }
                    });
                  }

                // format the payload  
                payload = {
                  ...sample,
                  [field]: this.form_data.test_package_samples[sample_index][field]
                };
              }
            }

            return payload;
          }
          return sample;
        });
    },
    deletePackageSample(index) {
      this.form_data.test_package_samples =
        this.form_data.test_package_samples.filter(
          (item, sample_index) => sample_index !== index
        );
    },
    submitForm() {
      this.$emit("submit", this.form_data);
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
